import { FC, useEffect, useMemo } from 'react';
import { Button, Spin } from 'antd';
import { useHistory } from 'react-router-dom';
import {
  CalendarOutlined,
  ClockCircleOutlined,
  TagOutlined,
  UserOutlined,
} from '@ant-design/icons';

import { APP_URL } from '../../constants/common-app.const';
import useGetCourses from '../../hooks/useGetCourses';
import { htmlToText, sanitizeHtml } from '../../utils/html.util';
import css from './CoursesCards.module.css';

const AVAILABLE_FOR_TAG: Record<string, string> = {
  legal: 'Юрлица',
  ip: 'ИП',
  individual: 'Физлица',
};

interface Props {
  search?: string;
  availableFor?: string[];
  onCount?: (n: number) => void;
}

const CoursesCards: FC<Props> = ({ search = '', availableFor = [], onCount }) => {
  const history = useHistory();
  const { courses, loading } = useGetCourses();

  // Описание приходит из БД с разметкой. Рендерим его как HTML (белый список
  // тегов), а искать продолжаем по тексту — по сырой строке поиск находил бы
  // курсы по запросам вида «li» или «strong».
  const prepared = useMemo(
    () =>
      courses.map((c) => ({
        ...c,
        descriptionHtml: sanitizeHtml(c.description),
        descriptionText: htmlToText(c.description),
      })),
    [courses],
  );

  const filtered = useMemo(() => {
    let result = prepared;
    const q = search.trim().toLowerCase();
    if (q) {
      result = result.filter(
        (c) =>
          c.title.toLowerCase().includes(q) ||
          c.descriptionText.toLowerCase().includes(q),
      );
    }
    if (availableFor.length > 0) {
      const requiredTags = availableFor.map((v) => AVAILABLE_FOR_TAG[v]).filter(Boolean);
      result = result.filter((c) =>
        requiredTags.some((tag) => c.availableFor.includes(tag as any)),
      );
    }
    return result;
  }, [prepared, search, availableFor]);

  useEffect(() => {
    if (!loading && onCount) onCount(filtered.length);
  }, [filtered.length, loading, onCount]);

  if (loading) {
    return (
      <div className={css.Grid}>
        <Spin />
      </div>
    );
  }

  if (!filtered.length) {
    return (
      <div className={css.Grid}>
        {courses.length === 0
          ? 'Доступных курсов пока нет. Следите за обновлениями.'
          : 'Курсов по заданным фильтрам не найдено.'}
      </div>
    );
  }

  return (
    <div className={css.Grid}>
      {filtered.map((course) => (
        <div key={course.id} className={css.Card}>
          <div className={css.Body}>
            <h3 className={css.Title}>{course.title}</h3>
            {/* div, а не p: описание может содержать <p>/<ul>, а блочные теги
                внутри <p> — невалидный HTML, браузер рвёт разметку. */}
            <div
              className={css.Description}
              dangerouslySetInnerHTML={{ __html: course.descriptionHtml }}
            />

            <div className={css.Divider} />

            <div className={css.Row}>
              <ClockCircleOutlined className={css.RowIcon} />
              <span className={css.RowLabel}>Длительность</span>
              <span className={css.RowValue}>{course.duration}</span>
            </div>

            <div className={css.Row}>
              <TagOutlined className={css.RowIcon} />
              <span className={css.RowLabel}>Цена за слушателя</span>
              <span className={css.RowValue}>{course.pricePerStudent}</span>
            </div>

            <div className={css.Row}>
              <CalendarOutlined className={css.RowIcon} />
              <span className={css.RowLabel}>Ближайшая группа</span>
              <span className={css.RowValue}>{course.groupPeriod}</span>
            </div>

            <div className={css.AvailableRow}>
              <UserOutlined className={css.RowIcon} />
              <span className={css.RowLabel}>Доступно для</span>
              <div className={css.Tags}>
                {course.availableFor.map((tag) => (
                  <span key={tag} className={css.Tag}>
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <Button
              className={css.SubmitBtn}
              onClick={() =>
                history.push(
                  APP_URL.COURSE_ENROLLMENT.replace(':id', course.id),
                  { title: course.title, priceKopecks: course.priceKopecks, format: course.format, duration: course.duration, allowedClientTypes: course.allowedClientTypes }
                )
              }
            >
              Оформить
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CoursesCards;
